import { CrmType } from "@/types/crm-settings";
import { UserGuideId } from "./user-guides";

export const links = {
  bot: {
    index: "/bot",
    name: "/bot-name",
    sendJoinMessage: "/bot-settings/send-join-message",
  },
  user: {
    index: "/user",
    changePassword: "/user/change-password",
    userById: (userId: string | number) => `/user/${userId}`,
    userByExternalId: (externalUserId: string | number) =>
      `/user/external/${externalUserId}`,
  },
  firm: {
    index: "/firm",
    firmById: (firmId: string | number) => `/user/${firmId}`,
  },
  featureFlags: {
    index: "/feature-flag",
    public: "/feature-flag/public",
    featureFlagByName: (featurename: string) => `/feature-flag/${featurename}`,
  },
  roleAssignment: {
    index: "/RoleAssignment",
    roleAssignmentById: (userId: string | number, role: string) =>
      `/RoleAssignment/${userId}/${role}`,
  },
  client: {
    index: "/client",
    clientById: (clientId: string | number) => `/client/${clientId}`,
    clientConversationById: (clientId: string | number) =>
      `/client/${clientId}/conversation`,
    emails: (clientId: string | number) => `/client/${clientId}/email`,
    emailById: (clientId: string | number, emailId: string | number) =>
      `/client/${clientId}/email/${emailId}`,
    emailSetPrimary: (clientId: string | number, emailId: string | number) =>
      `/client/${clientId}/email/${emailId}/primary`,
    phones: (clientId: string | number) => `/client/${clientId}/phone`,
    phoneById: (clientId: string | number, phoneId: string | number) =>
      `/client/${clientId}/phone/${phoneId}`,
    phoneSetPrimary: (clientId: string | number, phoneId: string | number) =>
      `/client/${clientId}/phone/${phoneId}/primary`,
  },
  calendar: {
    index: "/calendar",
    calendarById: (calendarId: string | number) => `/calendar/${calendarId}`,
  },
  conversation: {
    index: "/conversation",
    conversationById: (conversationId: string | number) =>
      `/conversation/${conversationId}`,
    participant: (conversationId: string | number) =>
      `/conversation/${conversationId}/participant`,
    participantById: (conversationId: string | number, participantId: string) =>
      `/conversation/${conversationId}/participant/${participantId}`,
    emailFollowUpByConversationId: (conversationId: string | number) =>
      `/conversation/${conversationId}/email-followup`,
    createPDF: (conversationId: string | number) =>
      `/conversation/${conversationId}/export-pdf`,
    reprocess: (conversationId: string | number) =>
      `/conversation/${conversationId}/reprocess`,
    prefillPdfTemplate: (conversationId: string, templateId: string) =>
      `/conversation/${conversationId}/template/pdf/${templateId}`,
    prefillExcelTemplate: (conversationId: string, templateId: string) =>
      `/conversation/${conversationId}/template/excel/${templateId}`,
    suggestedParticipants: (conversationId: string) =>
      `/conversation/${conversationId}/suggested-participant`,
  },
  conversationSegment: {
    index: "/conversation-segment",
    byConversationId: (conversationId: string | number) =>
      `/conversation-segment/${conversationId}`,
    byId: (conversationId: string | number, segmentId: string | number) =>
      `/conversation-segment/${conversationId}/${segmentId}`,
    mergeSpeakers: (conversationId: string) =>
      `/conversation-segment/${conversationId}/merge`,
  },
  conversationDataTableRow: {
    index: "/conversation-data-table-row",
    byId: (rowId: string) => `/conversation-data-table-row/${rowId}`,
    byConversationId: (conversationId: string | number) =>
      `/conversation-data-table-row/conversation/${conversationId}`,
  },
  participant: {
    index: "/participant",
    participantById: (participantId: string) => `/participant/${participantId}`,
  },
  conversationTask: {
    index: "/conversation-task",
    conversationTaskById: (conversationTaskId: string | number) =>
      `/conversation-task/${conversationTaskId}`,
    conversationTaskByConversationId: (conversationId: string | number) =>
      `/conversation-task/${conversationId}`,
  },
  meeting: {
    index: "/meeting",
    count: "/meeting/count",
    meetingById: (meetingId: string | number) => `/meeting/${meetingId}`,
    meetingByIdRecorded: (meetingId: string | number) =>
      `/meeting/${meetingId}/recorded`,
  },
  suggestedParticipant: {
    index: "/suggested-participant",
  },
  meetingPrep: {
    index: "/meeting-prep",
    meetingPrepById: (meetingPrepId: string | number) =>
      `/meeting-prep/${meetingPrepId}`,
    meetingPrepByIdEmail: (meetingPrepId: string | number) =>
      `/meeting-prep/${meetingPrepId}/email`,
  },
  meetingPrepEmail: {
    index: "/meeting-prep-email",
    meetingPrepEmailById: (meetingPrepEmailId: string | number) =>
      `/meeting-prep-email/${meetingPrepEmailId}`,
    meetingPrepEmailByMeetingId: (meetingId: string | number) =>
      `/meeting-prep-email/meeting/${meetingId}`,
    meetingPrepEmailByPrepId: (meetingPrepId: string | number) =>
      `/meeting-prep-email/prep/${meetingPrepId}`,
  },
  callback: {
    index: "/callback",
    oauthByPlatform: (platform: string) => `/callback/${platform}`,
  },
  clientLookup: {
    index: "/client-lookup",
    clientLookupById: (clientId: string | number) =>
      `/client-lookup/${clientId}`,
  },
  installedintegration: {
    index: "/installedintegration",
    installedIntegrationById: (id: string) => `/installedintegration/${id}`,
  },
  emailDraft: {
    index: "/email-draft",
  },
  paragon: {
    index: "/paragon",
    jwt: "/paragon/jwt",
    syncStatus: "/paragon/sync-status",
  },
  paragonConfig: {
    index: "/paragon-config",
  },
  // Firm-level configuration for non-Paragon integrations (e.g. LAYLAH).
  crmConfiguration: {
    index: "/crm-configuration",
  },
  userRecordingSetting: {
    index: "/user-recording-setting",
    userRecordingById: (userId: string | number) =>
      `/user-recording-setting/${userId}`,
  },
  userMeetingDomainSetting: {
    index: "/user-meeting-domain-setting",
    userMeetingDomainSettingById: (userId: string) =>
      `/user-meeting-domain-setting/${userId}`,
  },
  phoneNumber: {
    index: "/phonenumber",
    phoneNumberList: (userId: string | number) =>
      `/phonenumber?userId=${userId}`,
    phoneNumberById: (id: string) => `/phonenumber/${id}`,
  },
  userCrmSettings: {
    index: "/crm-settings",
    connectRedtail: `crm/redtail/connect`,
  },
  record: {
    index: "/record",
    recordUpload: "/record/upload",
    recordConversation: "/record/conversation",
    recordInit: "/record/conversation/init",
    pause: (uploadId: string) => `/record/conversation/pause/${uploadId}`,
    recordChunk: (uploadId: string, chunkIndex: number) =>
      `/record/conversation/chunk/${uploadId}/${chunkIndex}`,
    recordComplete: (uploadId: string) =>
      `/record/conversation/complete/${uploadId}`,
    recordCompletePartial: (uploadId: string) =>
      `/record/conversation/complete-partial/${uploadId}`,
    incomplete: (uploadId: string) => `/record/incomplete/${uploadId}`,
    incompleteScan: "/record/incomplete",
    recordById: (id: string) => `/record/conversation/${id}`,
    recordInitDirect: "/record/conversation/init-direct",
    refreshSas: (uploadId: string) =>
      `/record/conversation/refresh-sas/${uploadId}`,
    recordCompleteDirect: (uploadId: string) =>
      `/record/conversation/complete-direct/${uploadId}`,
    chunkManifest: (uploadId: string) =>
      `/record/conversation/${uploadId}/manifest`,
  },
  crm: {
    index: "/crm",
    accounts: "/crm/accounts",
    activities: "/crm/activities",
    contacts: "/crm/contacts",
    note: "/crm/note",
    users: "/crm/users",
    usersCurrent: "/crm/users/current",
    userById: (crmUserId: string) => `/crm/users/${encodeURIComponent(crmUserId)}`,
    settings: "/crm/settings",
    settingsByType: (type: CrmType) => `/crm/settings/${type.toLowerCase()}`,
    pushSummary: (conversationId: string, relatedTo: string) =>
      `/crm/push-summary/${conversationId}/${relatedTo}`,
    pushTask: (conversationTaskId: string) =>
      `/crm/push-task/${conversationTaskId}`,
  },
  emoney: {
    isConnected: "/e-money/is-connected",
  },
  conquest: {
    connect: "/customer-admin/conquest/connect",
  },
  auth: {
    index: "/",
    login: "/login",
    forgotPassword: "/forgot-password",
    resetPassword: "/reset-password",
    resetPasswordDiscovery: "/reset-password/discovery",
    organizationLookup: "/organization-lookup",
    passwordStrengthCheck: "/password-strength-check",
    oauth: "/oauth",
    callback: "/callback",
    session: "/session",
    sessionExchange: "/session/exchange",
    oauthCallback: "/oauth/callback",
    ssoCallback: "/sso/callback",
    ssoConnection: "/sso/connection",
    mfaTotpEnroll: "/mfa/totp/enroll",
    mfaTotpVerify: "/mfa/totp/verify",
    mfaOtpCreate: "/mfa/otp/create",
    mfaOtpVerify: "/mfa/otp/verify",
    mfaTotpDelete: "/mfa/totp/delete",
    logout: "/logout",
    discovery: {
      index: "/discovery",
      magicLinks: "/discovery/magic-links",
    },
    recoveryCodes: {
      index: "/recovery-codes",
      authenticate: "/recovery-codes/authenticate",
    },
    sso: {
      saml: "/v1/b2b/sso/saml",
      oidc: "/v1/b2b/sso/oidc",
      redirect: "/sso/redirect",
    },
  },
  log: {
    index: "/log",
  },
  chat: {
    index: "/chat",
    initConversation: (conversationId: string) =>
      `/chat/conversation/${conversationId}`,
    initClient: (clientId: string) => `/chat/client/${clientId}`,
    askByChatId: (chatId: string) => `/chat/ask-question/${chatId}`,
    initializeConversation: (conversationId: string) =>
      `/chat/initialize/conversation/${conversationId}`,
    initializeClient: (clientId: string) =>
      `/chat/initialize/client/${clientId}`,
  },
  userLanguageSetting: {
    index: "/user-language-setting",
  },
  userEmailSetting: {
    index: "/user-email-setting",
  },
  userEmailSignatureImage: {
    index: "/user-email-signature-image",
    byId: (id: string) => `/user-email-signature-image/${id}`,
    content: (id: string) => `/user-email-signature-image/${id}/content`,
  },
  summarizerSettings: {
    index: "/summarizer-settings",
    customSection: "/summarizer-settings/custom-section",
    customSectionById: (id: string) =>
      `/summarizer-settings/custom-section/${encodeURIComponent(id)}`,
    summarizerSettingById: (id: string) => `/summarizer-settings/${id}`,
    summarizerSettingByKey: (purpose: string, name: string) =>
      `/summarizer-settings/${encodeURIComponent(purpose)}/${encodeURIComponent(name)}`,
    purpose: (purpose: string) => `/summarizer-settings/purpose/${purpose}`,
  },
  dataRetention: {
    index: "/data-retention",
    dataRetentionById: (id: string) => `/data-retention/${id}`,
  },
  customPrompt: {
    index: "/custom-prompt",
    customPromptById: (id: string) => `/custom-prompt/${id}`,
  },
  postProcessingPrompt: {
    index: "/post-processing-prompt",
  },
  speakerMetadata: {
    index: "/speaker-metadata",
    byConversationId: (conversationId: string | number) =>
      `/speaker-metadata/conversation/${conversationId}`,
  },
  emailTemplate: {
    index: "/email-template",
    default: "/email-template/default",
    defaultById: (id: string) => `/email-template/${id}/default`,
    emailTemplateById: (id: string) => `/email-template/${id}`,
  },
  followUpEmailTemplate: {
    index: "/followup-email-template",
    followUpEmailTemplateById: (id: string) => `/followup-email-template/${id}`,
  },
  checkout: {
    checkout: "/checkout",
    success: "/checkout/success",
    resendWelcome: "/checkout/resend-welcome",
  },
  billing: {
    stateSummary: "/billing/state/summary",
  },
  coaching: {
    byConversationId: (conversationId: string | number) =>
      `/coaching/${conversationId}`,
    metrics: "/coaching/metrics",
  },
  dataExtraction: {
    statusByConversationId: (conversationId: string | number) =>
      `/data-extraction/${conversationId}/status`,
  },
  customerAdmin: {
    coaching: "/customer-admin/coaching",
    users: "/customer-admin/users",
  },
  document: {
    index: "/document/pdf",
    pdfById: (templateId: string) => `/document/pdf/${templateId}`,
    fillPdf: (templateId: string) => `/document/pdf/${templateId}/fill`,
    excel: "/document/excel",
    excelById: (templateId: string) => `/document/excel/${templateId}`,
    fillExcel: (templateId: string) => `/document/excel/${templateId}/fill`,
  },
  formFill: {
    upload: "/form-fill/upload",
    index: "/form-fill",
    byId: (id: string) => `/form-fill/${id}`,
    values: (id: string) => `/form-fill/${id}/values`,
    fill: (id: string) => `/form-fill/${id}/fill`,
    generate: (id: string) => `/form-fill/${id}/generate`,
    pageImage: (templateId: string, page: number) =>
      `/form-fill/page/${templateId}/${page}`,
  },
  // focalmonolith-owned deliverable records. The document-service and
  // agent-service deliverable endpoints are reached through their own proxies.
  deliverable: {
    index: "/deliverable",
    brandingDefaults: "/deliverable/branding/defaults",
    byId: (id: string) => `/deliverable/${id}`,
    revisions: (id: string) => `/deliverable/${id}/revision`,
    rebuild: (id: string) => `/deliverable/${id}/rebuild`,
    // PUT replaces the corpus, POST appends to it, GET returns it *with* the
    // extracted text — the one read that carries megabytes.
    sources: (id: string) => `/deliverable/${id}/source`,
    assets: (id: string) => `/deliverable/${id}/asset`,
  },
  deliverableTemplate: {
    index: "/deliverable-template",
    byId: (id: string) => `/deliverable-template/${id}`,
  },
  pylon: {
    identityHash: "/pylon/identity-hash",
  },
  meetingFilterRuleGroup: {
    index: "/meeting-filter-rule-group",
    byId: (id: string) => `/meeting-filter-rule-group/${id}`,
    reorder: "/meeting-filter-rule-group/reorder",
    rules: (groupId: string) =>
      `/meeting-filter-rule-group/${groupId}/rule`,
    ruleById: (groupId: string, ruleId: string) =>
      `/meeting-filter-rule-group/${groupId}/rule/${ruleId}`,
  },
  attachment: {
    initDirect: "/attachment/init-direct",
    register: "/attachment",
    byId: (id: string) => `/attachment/${id}`,
  },
  userGuideCompletion: {
    index: "/user-guide-completion",
    byGuide: (guide: UserGuideId) => `/user-guide-completion/${guide}`,
  },
};
